import React, { useEffect } from "react";

// import Router
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";

import "./App.css";
import "./style.css";

// import Component
import Header from "./components/Header";
import Footer from "./components/Footer";

// import Pages
import Home from "./Pages/Home";
import StartDream from "./Pages/StartDream";
import Login from "./Pages/Login/LoginView";
import ChangePassword from "./Pages/Login/ChangePassword";
import DeleteAccount from "./Pages/Login/DeleteAccount";
import ForgotPassword from "./Pages/Login/ForgotPassword";
import ForgotPasswordComplete from "./Pages/Login/ForgotPasswordComplete";
import DeleteGoal from "./Pages/Goal/DeleteGoal";
import MemberRegisterView from "./Pages/Login/MemberRegisterView";
import MemberSecretRegisterView from "./Pages/Login/MemberSecretRegisterView";
import MemberRegisterCompleteView from "./Pages/Login/MemberRegisterCompleteView";
import MyPage from "./Pages/Login/MyPage";
import Register from "./Pages/Register/Register";
import EditGoalRegister from "./Pages/Register/EditGoalRegister";
import EditGoalRegisterCompleteView from "./Pages/Register/EditGoalRegisterCompleteView";
import EditGoalRegisterConfirmView from "./Pages/Register/EditGoalRegisterConfirmView";
import DeleteGoalRegister from "./Pages/Register/DeleteGoalRegister";
import DeleteGoalRegisterCompleteView from "./Pages/Register/DeleteGoalRegisterCompleteView";
import RegisterConfirmView from "./Pages/Register/RegisterConfirmView";
import RegisterCompleteView from "./Pages/Register/RegisterCompleteView";
import RegisterSearchView from "./Pages/Research/RegisterSearchView";
import RegisterSearchResult from "./Pages/Research/RegisterSearchResult";
import { UserProvider } from "./Pages/Login/UserContext";
import { GoalProvider } from "./Pages/Register/GoalContext";
import { GoalListProvider } from "./Pages/Register/GoalListContext";
import BoardDetailPage from "./Pages/Goal/BoardDetailPage";
import GoalList from "./Pages/Goal/GoalList";
import SupportedGoals from "./Pages/Goal/SupportedGoals";
import Site from "./Pages/Site/Site";
import TermsOfService from "./Pages/Site/TermsOfService";
import Contact from "./Pages/Site/Contact";
import AiCommentGuide from "./Pages/Site/AiCommentGuide";
import AssetCredits from "./Pages/Site/AssetCredits";
import RankingPage from "./Pages/Ranking/RankingPage";
import AchievedRankingPage from "./Pages/Ranking/AchievedRankingPage";
import { trackPageView } from "./utils/analytics";

const resolvePageTitle = (pathname) => {
  if (pathname === "/") return "Mubo | 笑われる夢から、世界は変わる。";
  if (pathname.startsWith("/boards/")) return "夢の掲示板 | Mubo";
  if (pathname === "/Ranking") return "ランキング | Mubo";
  if (pathname === "/AchievedRanking") return "達成者ランキング | Mubo";
  if (pathname === "/Register") return "夢を投稿 | Mubo";
  if (pathname === "/RegisterConfirmView") return "投稿内容の確認 | Mubo";
  if (pathname === "/RegisterCompleteView") return "夢を公開しました | Mubo";
  if (pathname === "/RegisterSearchView" || pathname === "/RegisterSearchResult") return "夢を探す | Mubo";
  if (pathname === "/MyPage") return "マイページ | Mubo";
  if (pathname === "/SupportedGoals") return "応援している夢 | Mubo";
  if (pathname === "/my-goals") return "自分の夢 | Mubo";
  if (pathname === "/StartDream") return "夢を一行から | Mubo";
  if (pathname === "/Login") return "ログイン | Mubo";
  if (pathname === "/MemberRegisterView" || pathname === "/MemberSecretRegisterView") return "会員登録 | Mubo";
  if (pathname === "/MemberRegisterCompleteView") return "会員登録完了 | Mubo";
  if (pathname === "/ForgotPassword") return "パスワード再設定 | Mubo";
  if (pathname === "/ForgotPasswordComplete") return "再設定メール送信完了 | Mubo";
  if (pathname === "/ChangePassword") return "パスワード変更 | Mubo";
  if (pathname === "/DeleteAccount") return "アカウント削除 | Mubo";
  if (pathname === "/EditGoalRegister") return "夢を編集 | Mubo";
  if (pathname === "/EditGoalRegisterConfirmView") return "夢の編集内容確認 | Mubo";
  if (pathname === "/EditGoalRegisterCompleteView") return "夢を更新しました | Mubo";
  if (pathname === "/DeleteGoalRegister" || pathname === "/DeleteGoal") return "夢を削除 | Mubo";
  if (pathname === "/DeleteGoalRegisterCompleteView") return "夢を削除しました | Mubo";
  if (pathname === "/Site") return "Muboとは | Mubo";
  if (pathname === "/TermsOfService") return "利用規約 | Mubo";
  if (pathname === "/Contact") return "運営への問い合わせ | Mubo";
  if (pathname === "/AiCommentGuide") return "AIコメントについて | Mubo";
  if (pathname === "/AssetCredits") return "素材と権利表記 | Mubo";
  return "Mubo | 笑われる夢から、世界は変わる。";
};

function RouteTitleManager() {
  const location = useLocation();

  useEffect(() => {
    const title = resolvePageTitle(location.pathname);
    document.title = title;
    trackPageView({
      path: `${location.pathname}${location.search}`,
      title,
    });
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  return (
    <Router>
      <RouteTitleManager />
      <UserProvider>
        <GoalProvider>
          <GoalListProvider>
            <Header />
            <div className="container">
              <Routes>
                <Route exact path="/" element={<Home />} />
                <Route exact path="/Login" element={<Login />} />
                <Route exact path="/ChangePassword" element={<ChangePassword />} />
                <Route exact path="/ForgotPassword" element={<ForgotPassword />} />
                <Route exact path="/ForgotPasswordComplete" element={<ForgotPasswordComplete />} />
                <Route exact path="/DeleteAccount" element={<DeleteAccount />} />
                <Route exact path="/DeleteGoal" element={<DeleteGoal />} />
                <Route exact path="/MemberRegisterView" element={<MemberRegisterView />} />
                <Route exact path="/MemberSecretRegisterView" element={<MemberSecretRegisterView />} />
                <Route exact path="/MemberRegisterCompleteView" element={<MemberRegisterCompleteView />} />
                <Route exact path="/MyPage" element={<MyPage />} />
                <Route exact path="/SupportedGoals" element={<SupportedGoals />} />
                <Route exact path="/StartDream" element={<StartDream />} />
                <Route exact path="/Register" element={<Register />} />
                <Route exact path="/EditGoalRegister" element={<EditGoalRegister />} />
                <Route exact path="/EditGoalRegisterCompleteView" element={<EditGoalRegisterCompleteView />} />
                <Route exact path="/EditGoalRegisterConfirmView" element={<EditGoalRegisterConfirmView />} />
                <Route exact path="/DeleteGoalRegister" element={<DeleteGoalRegister />} />
                <Route exact path="/DeleteGoalRegisterCompleteView" element={<DeleteGoalRegisterCompleteView />} />
                <Route exact path="/RegisterConfirmView" element={<RegisterConfirmView />} />
                <Route exact path="/RegisterCompleteView" element={<RegisterCompleteView />} />
                <Route exact path="/RegisterSearchView" element={<RegisterSearchView />} />
                <Route exact path="/RegisterSearchResult" element={<RegisterSearchResult />} />
                <Route exact path="/boards/:id" element={<BoardDetailPage />} />
                <Route exact path="/my-goals" element={<GoalList />} />
                <Route exact path="/Ranking" element={<RankingPage />} />
                <Route exact path="/AchievedRanking" element={<AchievedRankingPage />} />
                <Route exact path="/Site" element={<Site />} />
                <Route exact path="/TermsOfService" element={<TermsOfService />} />
                <Route exact path="/Contact" element={<Contact />} />
                <Route exact path="/AiCommentGuide" element={<AiCommentGuide />} />
                <Route exact path="/AssetCredits" element={<AssetCredits />} />
              </Routes>
            </div>
            <Footer />
          </GoalListProvider>
        </GoalProvider>
      </UserProvider>
    </Router>
  );
}

export default App;
